import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, UntypedFormArray, UntypedFormBuilder, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import Decimal from 'decimal.js';
import { BehaviorSubject, Observable, forkJoin } from 'rxjs';
import { DialogTypeEnum, getDialogTypeAddedQtySign } from 'src/app/dashboard/interfaces/DialogType';
import { IMultipleDrugsForm } from 'src/app/dashboard/interfaces/IMultipleDrugsForm';
import { highlightRequiredControlsWithErrors } from 'src/app/shared/helpers/helpers';
import { ActivityTypesEnum } from 'src/app/shared/interfaces/ActivityTypesEnum';
import { BadgeColorsEnum } from 'src/app/shared/interfaces/BadgeColorsEnum';
import { ICurrentInventoryOnLocation } from 'src/app/shared/interfaces/ICurrentInventoryOnLocation';
import { IDrugInTable } from 'src/app/shared/interfaces/IDrugInTable';
import { IInventoryModel } from 'src/app/shared/interfaces/IInventoryModel';
import { ILocationToExclude } from 'src/app/shared/interfaces/ILocationToExclude.interface';
import { IInventoryLocation } from 'src/app/shared/interfaces/inventory-location.interface';
import { IViolatingTransactionDateNdc } from 'src/app/shared/interfaces/violating-transaction-date-ndc.interface';
import { FormattedDatePipe } from 'src/app/shared/pipes/formatted-date/formatted-date.pipe';
import { AlertMessageService } from 'src/app/shared/services/alert-message.service';
import { BackCountService } from 'src/app/shared/services/back-count.service';
import { DupTransactionsService } from 'src/app/shared/services/dup-transactions.service';
import { InventoryLocationsService } from 'src/app/shared/services/inventory-locations.service';
import { TransactionsService } from 'src/app/shared/services/transactions.service';
import { GetInventoryOnLocation, ValidateTransactionDateForInventories } from 'src/app/shared/states/inventory/inventory.actions';
import { InventoryState } from 'src/app/shared/states/inventory/inventory.state';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { ResetPotentialsDuplicates, TestForDupTransactions } from 'src/app/shared/states/transactions/transactions.actions';
import { FormControls } from 'src/app/shared/utilities/form-controls.util';
import { DrugProcessingBaseDirective } from '../drug-processing-base.directive';
import { DrugsProcessingService } from '../drugs-processing.service';


@UntilDestroy()
@Component({
  selector: 'app-multiple-drugs-dialog-base',
  templateUrl: './multiple-drugs-dialog-base.component.html',
  styleUrls: ['./multiple-drugs-dialog-base.component.scss', './../user-board-dialog.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [InventoryLocationsService, BackCountService]
})
export class MultipleDrugsDialogBaseComponent extends DrugProcessingBaseDirective implements OnInit, OnDestroy {

  @Input() header: string;
  @Input() drugScannerLabel: string;
  @Input() descriptionLabel: string = 'Describe what happened';
  @Input() dialogType: DialogTypeEnum = DialogTypeEnum.Dispense;
  @Input() activityType: ActivityTypesEnum = ActivityTypesEnum.Dispensed;
  @Input() submitBtnText: string;
  @Input() limitDrugSearchToPharmacy: boolean = true;

  @Input() hideHeaderUnderline: boolean;

  @Input() selectedDrug = undefined;
  @Input() maxValueExceededErrorMessage: string;

  @Input() excludedLocations: string[];
  

  @Select(PharmacyState.enableC3toC5Drugs) enableC3toC5Drugs$: Observable<boolean>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;
  @Select(InventoryState.primaryLocationID) primaryLocationID$ : Observable<string>;
  @Select(InventoryState.currentInventoryForNdcOnLocation) currentInventoryForNdcOnLocation$ : Observable<ICurrentInventoryOnLocation>;
  
  DialogTypeEnum = DialogTypeEnum;

  @ViewChild('rxNumberInput') rxNumberInput;
  @ViewChild('patientNameInput') patientNameInput;
  @ViewChild('descriptionInput') descriptionInput;
  @ViewChild('addedQuantityInput') addedQuantityInput;
  @ViewChild('submitButton') submitButton;
  @ViewChild('closeButton') closeButton;
  @ViewChild('transactionDatePicker') transactionDatePicker;
  @ViewChild('fillNumberInput') fillNumberInput;

  addDrugMode: boolean = true;
  searchSingleLocation: boolean = this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled);

  inventoryLocationsEnabled: boolean;

  forceDrugSearchError: boolean;
  drugSearchErrorMessage: string = 'No inventory exists';
  dialogId: string = 'drug-processing';

  hideSelf$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showDupeDispenseModal$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  typesForDispenseModal = [DialogTypeEnum.Dispense, DialogTypeEnum.DispenseInAuditAdjustment];

  BadgeColorsEnum = BadgeColorsEnum;

  dialogMaxWidth = 800;
  
  gpi: string = null;

  constructor(public store: Store, protected fb: UntypedFormBuilder, private cd: ChangeDetectorRef, private drugsService: DrugsProcessingService, 
    private formattedDatePipe: FormattedDatePipe, private transactionsService: TransactionsService, private dupTransactionsService: DupTransactionsService,
    private inventoryLocationsService: InventoryLocationsService, private backCountService: BackCountService, private alertService: AlertMessageService

  ) { 
    super(store, fb);
    this.dialogForm = this.fb.group({
      drugs: this.fb.array([]),
      rxNumber: '',
      patientName: '',
      description: '',
      transactionDateDisplayValue: formattedDatePipe.transform(new Date(), 'MMM d a, HH:mm yyyy'),
      transactionDate: new Date(),
      fillNumber: 0,
    } as { [key in keyof IMultipleDrugsForm]: any });
  }

  ngOnInit(): void {
    if (this.typesForDispenseModal.includes(this.dialogType)) {
      this.dialogForm.get('rxNumber').setValidators(Validators.required);
      this.dialogForm.get('patientName').setValidators(Validators.required);
      this.dialogForm.get('fillNumber').setValidators([Validators.required, Validators.min(0)]);
    }
    if (this.selectedDrug) {
      this.addDrugMode = false;
      this.searchSingleLocation = this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled);
      this.appendDrug(this.selectedDrug);
      this.focusFirstInput();
    }

    this.drugsService.searchScannerDrugSelected$
    .pipe(untilDestroyed(this))
    .subscribe(selected => {
      if(selected?.onHand !== undefined) {
        this.addSelectedOption(selected);
        this.cd.detectChanges();
      };
    })
  }

  dateTimeChanged(date): void {
    this.dialogForm.get('transactionDate')?.setValue(date?.value);
    this.markAddedQtyControlsWithNegativeBalanceErrorValid();
  }

  optionSelected(option: IInventoryModel): void {
    if (option && !this.showDrugSearchErrorIfDrugAlreadyAddedAndReturnTrue(option?.packageNdc, option?.locationID)) {
      this.inventoryLocationsService.addLocationsWithPositiveInventory(option.drugID, option.positiveInventoryBalanceLocationIDs);
      this.drugsService.searchScannerDrugSelected$.next(option);
      this.gpi = null;
    }
  }

  addSelectedOption(option: IInventoryModel): void {
    this.appendDrug(option);
    this.addDrugMode = false;
    this.searchSingleLocation = this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled);
    this.focusInputOfJustAddedDrug(this.getDrugsFormArray()?.length);
  }

  updateOnHandValueForNdc(locationId: string, drugId: string, onHand: number): void {
    const drugIdx = (this.getDrugsFormArray().value as any[]).findIndex(x => x?.inventoryLocationId === locationId && x?.drugId === drugId);
    if (drugIdx > -1) {
      const group = this.getDrugsFormArray().at(drugIdx);
      group?.get('onHand')?.setValue(onHand);
      if (this.dialogType !== DialogTypeEnum.Receive && this.dialogType !== DialogTypeEnum.ReceiveInAuditAdjustment ) {
        group?.get('addedQuantity').setValidators([Validators.required, Validators.min(0), Validators.max(onHand)]);
      }
      group?.get('addedQuantity').updateValueAndValidity();
      this.cd.detectChanges();
    }
  }

  appendDrug(drug: any): void {
    const drugs = this.getDrugsFormArray();
    const max = this.dialogType !== DialogTypeEnum.Receive ? 
      drug?.maxQty || drug.onHand : Number.MAX_VALUE;

    const backCountEnabledForTheActivity = this.backCountService.enabledForActivityAndSchedule(this.activityType, drug.schedule, drug.drugID ?? drug.id) &&
    this.dialogType !== DialogTypeEnum.DispenseInAuditAdjustment && this.dialogType !== DialogTypeEnum.OtherActionsInAuditAdjustment;

    const newTransaction = this.fb.group({
      drugId: drug.drugID ?? drug.id,
      friendlyName: drug.friendlyName,
      packageNdc: drug.packageNdc,
      addedQuantity: ['', [Validators.required, Validators.min(0), Validators.max(max)]],
      onHand: drug.onHand || 0,
      note: '',
      endingQuantity: [0, Validators.min(0)],
      inventoryLocationId: drug.locationID ?? drug?.inventoryLocationID ?? this.store.selectSnapshot(InventoryState.primaryLocationID),
      isBackCountMatches:  [null, backCountEnabledForTheActivity ? Validators.required : null],
      gpi: drug.gpi,
      actualSOH: drug.similarOnHand,
      availableSOH: drug.similarOnHand,
    });
    newTransaction.get('addedQuantity')
    .valueChanges
    .pipe(untilDestroyed(this))
    .subscribe(addedQuantity => {
      this.addedQtyControlChanged(newTransaction, addedQuantity);
      this.adjustSOH(drug.gpi);
    });

    drugs.push(newTransaction);
    this.adjustSOH(drug.gpi);
  }

  showDrugSearchErrorIfDrugAlreadyAddedAndReturnTrue(packageNdc: string, locationID: string): boolean {
    const drugs = this.getDrugsFormArray();
    if (drugs.controls.some(drug => drug.value.packageNdc.includes(packageNdc) && (!locationID || drug.value?.inventoryLocationId === locationID))) {
      this.drugSearchErrorMessage = "The drug is already added";
      this.forceDrugSearchError = true;
      return true;
    }
    this.forceDrugSearchError = false;
  }

  focusInputOfJustAddedDrug(drugsCount: number): void { 
    if(drugsCount === 1) { 
      this.focusFirstInput();
    } else {
      if (this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
        this.focuLocationInput(drugsCount -1);
      } else {
        this.focusCountInput(drugsCount - 1);
      }
    }
  }

  onAddNewDrugClick(): void {
    this.gpi = this.getDrugsFormArray().value[0]?.gpi;
    this.addDrugMode = true;
    this.focusInput('drugSearchInput');
  }

  deleteRow(index: number): void {
    const drugs = this.getDrugsFormArray();
    const drugID = drugs.at(index).value.drugId;
    const gpi = drugs.at(index).value.gpi;
    const shouldRemoveBackCountForTheDrug = (drugs.value)?.filter(x => x.drugId === drugID)?.length <= 1;
    if (shouldRemoveBackCountForTheDrug) {
      this.backCountService.removeDrugIDRequiringBackCount(drugID);
    }
    drugs.removeAt(index);
    this.dialogForm.setControl('drugs', drugs);
    this.dialogForm.updateValueAndValidity();
    this.adjustSOH(gpi);
  }

  private isAnyDup(): boolean {
    const formValue = this.dialogForm.getRawValue();
    return formValue?.drugs?.some(x => this.dupTransactionsService.isDup(x?.drugId, x.inventoryLocationId));
  }

  validate(): void {
    this.validateForm.next();
    if (this.dialogForm.invalid || !this.dialogForm.controls.drugs?.value?.length || this.addDrugMode) {
      highlightRequiredControlsWithErrors(this.dialogForm);
      //array
      this.getDrugsFormArray().controls?.forEach((group: FormGroup) => {
        highlightRequiredControlsWithErrors(group);
      });
      return;
    }

    if (this.dialogForm.valid) {
      const formValue = this.dialogForm.getRawValue();
      const payload = {
        transactionDate: formValue?.transactionDate,
        inventoryActions: formValue?.drugs?.map(x => ({drugId: x.drugId, addedQuantity: x.addedQuantity, 
          activityType: this.dialogType === DialogTypeEnum.Receive || this.dialogType === DialogTypeEnum.ReceiveInAuditAdjustment  ? ActivityTypesEnum.Received : ActivityTypesEnum.Dispensed,
          locationId : x.inventoryLocationId
        }))
      }
      const validateTrDateRequest$ = this.store.dispatch(new ValidateTransactionDateForInventories(payload));

      if(this.dialogType !== DialogTypeEnum.Dispense && this.dialogType !== DialogTypeEnum.DispenseInAuditAdjustment) {
        validateTrDateRequest$
        .pipe(untilDestroyed(this))
        .subscribe(resp => {
          const response = resp?.inventory?.response;
          if(response?.success && !response?.value?.length) {
           this.submit();
          } else if (response?.success) {
            this.markConflictingNdcs(response.value?.map(x => x.drugId));
            this.cd.detectChanges();
            this.transactionsService.handleTransactionDateErrors(response.value);
          }
        })
      } else {
        let requests = [validateTrDateRequest$];

        if (!this.isAnyDup()) {
          const testForDupesPayload = {
            rxNumber: formValue.rxNumber,
            transactionDate: formValue.transactionDate,
            fillNumber: formValue.fillNumber,
            ndcs : formValue.drugs.map(x => ({
              drugId: x.drugId,
              packageNdc: x.packageNdc,
              addedQuantity: 0 - Math.abs(x.addedQuantity)
            }))
          };
          const testForDupeDispenseRequest$ = this.store.dispatch(
            new TestForDupTransactions(testForDupesPayload)
          );
          requests.push(testForDupeDispenseRequest$);
        }

        forkJoin(requests)
          .pipe(untilDestroyed(this))
          .subscribe((results) => {
            const [response1, response2] = results;
            const validateTrDateResponse = response1.inventory?.response;
            let duplicateDispenses = response2?.transactions?.duplicateTransactions;
            if (
              validateTrDateResponse?.success &&
              validateTrDateResponse?.value?.length
            ) {
              this.markConflictingNdcs(validateTrDateResponse.value?.map((x) => ({ drugId: x.drugId, locationId: x.locationId})));
              this.cd.detectChanges();
              this.transactionsService.handleTransactionDateErrors(
                validateTrDateResponse.value
              );
            }
            else if (duplicateDispenses && duplicateDispenses?.length > 0) {
              this.hideSelf$.next(true);
              this.showDupeDispenseModal$.next(true);
            } else if (
              validateTrDateResponse?.success &&
              !validateTrDateResponse?.value?.length &&
              !duplicateDispenses?.length
            ) {
              this.submit();
            }
        });
      }
    }
  }

  private markConflictingNdcs(conflictingNDCs: IViolatingTransactionDateNdc[]) {
    const drugs = this.getDrugsFormArray();
    conflictingNDCs.forEach(ndc => {
      const drugControl = drugs.controls.find(control => control.value.drugId === ndc.drugId && control.value.inventoryLocationId === ndc.locationId);
      drugControl?.get('addedQuantity')?.setErrors({causedNegativeBalance: true});
      this.dialogForm.get('transactionDateDisplayValue')?.setErrors({causedNegativeBalance: true});
    });
  }

  private markAddedQtyControlsWithNegativeBalanceErrorValid(): void {
    this.getDrugsFormArray()?.controls?.forEach(control => {
      const addedQtyControl = control.get('addedQuantity');
      if (addedQtyControl.hasError('causedNegativeBalance')) {
        addedQtyControl.updateValueAndValidity();
      }
    });
  }

  private markTransactionDateControlWithNegativeBalanceErrorValid(): void {
    const control = this.dialogForm.get('transactionDateDisplayValue');
    if (control.hasError('causedNegativeBalance')) {
      control.updateValueAndValidity();
    }
  }

  submit() {
    this.save.emit(this.dialogForm.getRawValue());
   // this.dialogForm.reset();
    this.store.dispatch(new ResetPotentialsDuplicates());
  }

  saveAddedNote(note: string, i: number): void {
    this.getNoteControlOfDrugAt(i)?.setValue(note);
  }

  removeNote(i: number): void {
    this.getNoteControlOfDrugAt(i)?.setValue(null);
  }

  focusInput(inputName: string): void {
     setTimeout(() => this[inputName] && this[inputName]?.appInput?.nativeElement?.focus());
  }

  focusButton(buttonName: string): void {
    setTimeout(() => this[buttonName]?.focus());
  }

  onCountInputKeyDown(index: number): void {
    event?.preventDefault();
    //last input
    if(this.dialogForm.controls.drugs['controls']?.length - 1 === index) {
       if (this.dialogForm.valid) 
        this.focusButton('submitButton');
      else 
        this.focusButton('closeButton');

      return;
    }

    if (this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
      this.focuLocationInput(++index);
    } else {
      this.focusCountInput(++index);
    }

  }

  focusCountInput(index: number): void { 
    setTimeout(() => {
      (<HTMLElement>document.querySelector(`app-dialog#${this.dialogId} tbody`)?.querySelectorAll('input')[index])?.focus();
    });
  }

  focuLocationInput(index: number): void { 
    setTimeout(() => {
      (<HTMLElement>document.querySelector(`app-dialog#${this.dialogId} tbody`)?.querySelectorAll('app-inventory-locations-dropdown input')[index])?.focus();
    });
  }


  focusFirstInput(): void {
    event?.preventDefault();
    if (this.dialogType === DialogTypeEnum.Dispense || this.dialogType  === DialogTypeEnum.DispenseInAuditAdjustment) {
      this.focusInput('rxNumberInput');
    } if (this.dialogType === DialogTypeEnum.Receive || this.dialogType === DialogTypeEnum.ReceiveInAuditAdjustment) {
      this.transactionDatePicker?.focusInput();
    }
    else {
      this.focusInput('descriptionInput');
    }
  }

  onRxNumberInputTabEnterKeyDown(): void {
    this.fillNumberInput ? this.focusInput('fillNumberInput') : this.focusInput('patientNameInput');
  }

  drugSearchCleared() {
    this.forceDrugSearchError = false;
    this.gpi = null;
    this.drugSearchErrorMessage = '';
  }

  trackBy(index, item) {
    return item?.drugId + item?.locationId;
  }

  close() {
    if(this.dialogForm.controls.drugs['controls'].length > 0 && this.addDrugMode) { 
      this.addDrugMode = false;
      this.searchSingleLocation = this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled);
    } else {
      this.dialogForm.reset();
      this.cancel.emit();
      this.store.dispatch(new ResetPotentialsDuplicates());
    }
  }

  withAddDrugFeature(): boolean { 
    return this.dialogType === DialogTypeEnum.Dispense;
  }

  withDeleteDrugFeature(): boolean { 
    return this.dialogType !== DialogTypeEnum.DispenseInAuditAdjustment && this.dialogType !== DialogTypeEnum.OtherActionsInAuditAdjustment &&
    this.dialogType !== DialogTypeEnum.ReceiveInAuditAdjustment;
  }

  quantityViolatedError(): boolean { 
    return this.maxValueExceededErrorMessage && this.dialogForm.controls.drugs['controls'].some(drug => drug.controls.addedQuantity.errors?.max);
  }

  getDrugsFormArray(): FormArray<FormGroup<FormControls<IDrugInTable>>> {
    return this.dialogForm.controls.drugs as UntypedFormArray;
  }

  getNoteControlOfDrugAt(index: number): AbstractControl {
    return this.getDrugsFormArray().at(index)?.get('note');
  }
  
  withDescription(): boolean { 
    return this.dialogType !== DialogTypeEnum.Dispense && this.dialogType !== DialogTypeEnum.DispenseInAuditAdjustment && 
    this.dialogType !== DialogTypeEnum.Receive && this.dialogType !== DialogTypeEnum.ReceiveInAuditAdjustment;
  }

  now():Date {
    return new Date();
  }

  review(): void {
    this.showDupeDispenseModal$.next(false);
    this.hideSelf$.next(false);
  }

  getSubmitBtnText(): string {
    if(this.isAnyDup()) {
      return this.submitBtnText + ' (dup)';
    }
    return this.submitBtnText;
  }

  locationChosen(location: IInventoryLocation, drugId: string): void {
    this.store.dispatch(new GetInventoryOnLocation(location.id, drugId))
          .pipe(untilDestroyed(this))
          .subscribe(res => {
            const currentInventoryOnLocation = this.store.selectSnapshot(InventoryState.currentInventoryForNdcOnLocation);
            this.updateOnHandValueForNdc(currentInventoryOnLocation.locationID, currentInventoryOnLocation.drugID, currentInventoryOnLocation.onHand);
          });
  }

  getInputLocationColumnHeader(): string {
    switch(this.dialogType) {
      case DialogTypeEnum.Receive: 
        return 'Into location';
    }
    return 'From location';
  }

  public getLocationsToExclude(): ILocationToExclude [] {
    if (this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
      return [...this.getDefaultLocationsToExclude()?.map(id => ({id})), 
        ...this.getDrugsFormArray()?.value?.map(x => ({id: x?.inventoryLocationId, drugID: x?.drugId})) || []
        ]
      }
      return [];
    }


  public getDefaultLocationsToExclude(): string[] {
    if (this.dialogType === this.DialogTypeEnum.Dispense || this.dialogType === this.DialogTypeEnum.DispenseInAuditAdjustment ||
      this.dialogType === this.DialogTypeEnum.RecordSaleToProvider || this.dialogType === this.DialogTypeEnum.RecordDamagedOrExpired
    ) {
      return [this.store.selectSnapshot(InventoryState.damagedExpiredLocationID)];
    }
    return [];
  }

  public getLocationsToExcludeWhichAlreadySelectedForTheDropdown(rowN: number): string[] {
    const row = this.getDrugsFormArray()?.value?.[rowN];
    return [...this.getDefaultLocationsToExclude(), 
      ...this.getDrugsFormArray()?.value
      ?.filter(x => x?.inventoryLocationId !== row?.inventoryLocationId && x.drugId === row?.drugId)
      ?.map(x => x?.inventoryLocationId) || []
    ];
  }

  private addedQtyControlChanged(ndc: FormGroup, addedQtyNewValue: number) : void {
    const onHand = ndc.get('onHand').value;
    if(this.dialogType === DialogTypeEnum.Receive || this.dialogType === DialogTypeEnum.ReceiveInAuditAdjustment) { 
      ndc.get('endingQuantity').setValue(new Decimal(+onHand).add(new Decimal(+addedQtyNewValue)).toNumber());
    }
    else ndc.get('endingQuantity').setValue(new Decimal(+onHand).sub(new Decimal(+addedQtyNewValue)).toNumber());

    this.markTransactionDateControlWithNegativeBalanceErrorValid();
  }

  private adjustSOH(gpi: string): void {
   const rows = this.getDrugsFormArray()
    .controls.filter(control => control.value.gpi === gpi);
     if (rows?.length) {
           const actualSOH = new Decimal(+rows[0].value.actualSOH);
           const sign = getDialogTypeAddedQtySign(this.dialogType);
           let newSOH = rows
           .map(x => +x.value.addedQuantity)
           .reduce((acc, count) => acc.add(new Decimal(count).mul(sign)), actualSOH)
           .toNumber();
     
           rows.forEach(control => {
             const value = control.value;
             if (!!value.gpi) {
               control.get('availableSOH').setValue(newSOH);
             }
           });
      }
  }

  transactionDatePickerKeyDown(): void {
    if (this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
      this.focuLocationInput(0)
    } else {
      this.focusCountInput(0);
    }
  }

  getAddedQtyTotal(): number {
    return (<any[]>this.getDrugsFormArray().value)?.map(x => x.addedQuantity)?.reduce((prev, curr) => new Decimal(+prev).add(new Decimal(+curr)), 0);
  }

  ngOnDestroy(): void {
    this.drugsService.searchScannerDrugSelected$.next(null);
  }
}
